import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { TbMessages } from "react-icons/tb";
import { FiSearch } from "react-icons/fi";
import { AiOutlineClose } from "react-icons/ai";
import { isToday, isYesterday, subDays, isWithinInterval } from "date-fns";
import { useOnClickOutside } from "usehooks-ts";
import {
  MobileChatListDrawerWrapper,
  MobileChatListDrawerBody,
  MobileChatListDrawerClose,
  MobileChatListHeader,
  MobileChatListSearch,
  MobileChatListContent,
  MobileChatListItem,
  MobileChatListDateSeparator,
  MobileChatListNewChatButton,
} from "./MobileChatListDrawerStyles";
import { useChat } from "../../Common/Hooks/useChat";
import { Chat as ChatDTO } from "../../Common/Types-DTOs/Chat";
import { isErr } from "../../Common/Utils/Result";
import { eventBus } from "../../Common/EventBus";
import { Notification } from "../../Components/Notifications";
import { useAuth } from "../../Common/Hooks/useAuth";
import { formatChatGroupLabel } from "../../Common/Utils/dateUtils";

const getDateKey = (date: Date): string => {
  const now = new Date();
  const chatDate = new Date(date);

  if (isToday(chatDate)) return "today";
  if (isYesterday(chatDate)) return "yesterday";

  const weekAgo = subDays(now, 7);
  if (isWithinInterval(chatDate, { start: weekAgo, end: now })) return "week";

  const monthAgo = subDays(now, 30);
  if (isWithinInterval(chatDate, { start: monthAgo, end: now })) return "month";

  return "older";
};

interface MobileChatListDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const MobileChatListDrawer = ({ isOpen, setIsOpen }: MobileChatListDrawerProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [chats, setChats] = useState<ChatDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { getAllChats } = useChat();
  const { isLoggedIn } = useAuth();
  const ref = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  const handleClose = useCallback(() => {
    if (isOpen) setIsOpen(false);
  }, [isOpen, setIsOpen]);

  useOnClickOutside(ref, handleClose);

  useEffect(() => {
    isMountedRef.current = true;

    const loadChats = async () => {
      setIsLoading(true);
      const response = await getAllChats();

      if (!isMountedRef.current) {
        return;
      }

      if (isErr(response)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", t("common.headerMobile.chatListDrawer.errors.loadChatsFailed"))
        );
        setIsLoading(false);
        return;
      }

      setChats(response.value.conversations || []);
      setIsLoading(false);
    };

    if (isOpen && isLoggedIn) {
      loadChats();
    } else if (!isLoggedIn) {
      setIsLoading(false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [isOpen, location.pathname, isLoggedIn, getAllChats]);

  const filteredChats = useMemo(() => {
    if (!searchQuery) return chats;
    return chats.filter((chat) => {
      const firstMessage = chat.messages?.[0]?.text || "";
      return firstMessage.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [chats, searchQuery]);

  // Group chats by date
  const groupedChats = useMemo(() => {
    const groups: { dateKey: string; dateLabel: string; chats: ChatDTO[] }[] = [];

    // Sort chats by last message date (most recent first)
    const sortedChats = [...filteredChats].sort((a, b) => {
      const dateA = a.messages && a.messages.length > 0
        ? new Date(a.messages[a.messages.length - 1].creationDate).getTime()
        : 0;
      const dateB = b.messages && b.messages.length > 0
        ? new Date(b.messages[b.messages.length - 1].creationDate).getTime()
        : 0;
      return dateB - dateA;
    });

    sortedChats.forEach((chat) => {
      if (chat.messages && chat.messages.length > 0) {
        const lastMessage = chat.messages[chat.messages.length - 1];
        const dateKey = getDateKey(lastMessage.creationDate);
        const existingGroup = groups.find(g => g.dateKey === dateKey);

        if (existingGroup) {
          existingGroup.chats.push(chat);
        } else {
          groups.push({
            dateKey,
            dateLabel: formatChatGroupLabel(lastMessage.creationDate),
            chats: [chat],
          });
        }
      }
    });

    // Sort groups by priority (today first, then yesterday, etc.)
    const groupOrder = ['today', 'yesterday', 'week', 'month', 'older'];
    groups.sort((a, b) => groupOrder.indexOf(a.dateKey) - groupOrder.indexOf(b.dateKey));

    return groups;
  }, [filteredChats]);

  const getChatTitle = (chat: ChatDTO) => {
    if (chat.messages && chat.messages.length > 0) {
      const firstMessage = chat.messages[0].text || "";
      return firstMessage.length > 50 ? firstMessage.substring(0, 50) + "..." : firstMessage;
    }
    return t("common.headerMobile.chatListDrawer.untitledChat");
  };

  const getLastMessage = (chat: ChatDTO) => {
    if (chat.messages && chat.messages.length > 0) {
      const raw = chat.messages[chat.messages.length - 1].text || "";
      return raw
        .replace(/\[BR\]/g, " ")
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/(?<!\w)_(.+?)_(?!\w)/g, "$1")
        .replace(/~(.+?)~/g, "$1")
        .replace(/`(.+?)`/g, "$1")
        .replace(/\s{2,}/g, " ")
        .trim();
    }
    return "";
  };

  const currentChatId = location.pathname.split("/chat/")[1];

  const handleNewChat = () => {
    navigate("/chat");
    setIsOpen(false);
  };

  const handleChatClick = (chatId: string) => {
    navigate(`/chat/${chatId}`);
    setIsOpen(false);
  };

  return (
    <MobileChatListDrawerWrapper $isOpen={isOpen} ref={ref}>
      <MobileChatListDrawerBody>
        <MobileChatListDrawerClose>
          <span onClick={() => setIsOpen(false)}>
            <AiOutlineClose />
          </span>
        </MobileChatListDrawerClose>

        <MobileChatListHeader>
          <MobileChatListNewChatButton onClick={handleNewChat}>
            <TbMessages size={18} />
            {t("common.headerMobile.chatListDrawer.newChatButton")}
          </MobileChatListNewChatButton>
          <MobileChatListSearch>
            <FiSearch size={16} />
            <input
              type="text"
              placeholder={t("common.headerMobile.chatListDrawer.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </MobileChatListSearch>
        </MobileChatListHeader>

        <MobileChatListContent>
          {isLoading ? (
            <div
              style={{
                padding: "2rem 1rem",
                textAlign: "center",
                color: "#999",
                fontSize: "0.875rem",
              }}
            >
              {t("common.headerMobile.chatListDrawer.loadingChats")}
            </div>
          ) : groupedChats.length > 0 ? (
            groupedChats.map((group) => (
              <div key={group.dateKey}>
                <MobileChatListDateSeparator>{group.dateLabel}</MobileChatListDateSeparator>
                {group.chats.map((chat) => {
                  const isActive = chat.id === currentChatId;
                  return (
                    <MobileChatListItem
                      key={chat.id}
                      $isActive={isActive}
                      onClick={() => handleChatClick(chat.id)}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "inherit",
                          lineHeight: "1.4",
                          marginBottom: "0.25rem"
                        }}>
                          {getChatTitle(chat)}
                        </div>
                        {getLastMessage(chat) && (
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "inherit",
                              opacity: 0.7,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              lineHeight: "1.3",
                            }}
                          >
                            {getLastMessage(chat)}
                          </div>
                        )}
                      </div>
                    </MobileChatListItem>
                  );
                })}
              </div>
            ))
          ) : (
            <div
              style={{
                padding: "2rem 1rem",
                textAlign: "center",
                color: "#999",
                fontSize: "0.875rem",
              }}
            >
              {searchQuery
                ? t("common.headerMobile.chatListDrawer.noResults")
                : t("common.headerMobile.chatListDrawer.emptyState")}
            </div>
          )}
        </MobileChatListContent>
      </MobileChatListDrawerBody>
    </MobileChatListDrawerWrapper>
  );
};

